import { useLocalization } from "../../hooks/useLocalization";
import type { UpcomingMatch } from "../../types/types";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { HeroCountdown } from "./HeroCountdown";

const MOBILE_VIDEO_BREAKPOINT = "(max-width: 768px)";

interface HeroProps {
  remainingSeconds: number;
  loading: boolean;
  failed: boolean;
  noContent: boolean;
  nextUpcomingMatch?: UpcomingMatch;
  liveMatches: UpcomingMatch[];
}

export const Hero = ({
  remainingSeconds,
  loading,
  failed,
  noContent,
  nextUpcomingMatch,
  liveMatches,
}: HeroProps) => {
  const { strings } = useLocalization();
  const assets = import.meta.env.VITE_S3_ASSET_URL;
  const isMobile = useMediaQuery(MOBILE_VIDEO_BREAKPOINT);
  const enableVideo = !isMobile;

  return (
    <section
      className="relative isolate m-0 flex w-full min-h-40 self-stretch overflow-visible bg-transparent"
      aria-label={strings.TITLE}
    >
      <div
        className="pointer-events-none absolute top-[-12vh] left-1/2 z-0 h-[calc(100%+12vh)] w-screen -translate-x-1/2 overflow-hidden bg-page"
        aria-hidden="true"
      >
        <div className="absolute inset-0 origin-top scale-110" aria-hidden="true">
          <img
            src={`${assets}/images/home-bg.png`}
            alt=""
            width={1920}
            height={1080}
            sizes="100vw"
            className="h-full w-full object-cover object-top"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </div>
        {enableVideo ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full origin-top scale-110 object-cover object-top max-md:hidden"
          >
            <source src={`${assets}/videos/FPL_Hidastettu.mp4`} type="video/mp4" />
          </video>
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-b from-[#10101d]/5 via-[#10101d]/20 to-[#10101d]/65" />
      </div>

      <div
        className="pointer-events-none absolute bottom-0 left-1/2 z-[1] h-[clamp(4rem,10vh,8rem)] w-screen -translate-x-1/2 bg-gradient-to-b from-transparent to-page"
        aria-hidden="true"
      />

      <div className="relative z-[2] mx-auto flex w-full max-w-7xl flex-1 flex-col gap-[clamp(1.5rem,3vh,2.5rem)] px-[var(--padding-view)] pb-[clamp(2rem,4vh,3rem)] pt-[calc(5.5rem+2rem)] max-md:gap-6 max-md:pb-8 max-md:pt-[calc(5rem+1.5rem)] max-[480px]:gap-5 max-[480px]:pt-[calc(4.75rem+1.25rem)]">
        <div className="relative grid min-h-[clamp(24rem,57vh,36rem)] grid-rows-[auto_1fr] items-stretch max-md:min-h-[clamp(20rem,50vh,28rem)] max-[480px]:min-h-[clamp(19rem,48vh,26rem)]">
          <div
            className="pointer-events-none absolute inset-0 z-0 overflow-visible"
            aria-hidden="true"
          >
            <img
              className="absolute top-0 right-[clamp(-1rem,-2vw,0rem)] h-full w-auto max-w-[min(58vw,40rem)] object-contain object-right-top opacity-[0.38] mix-blend-screen max-md:right-[-10%] max-md:max-w-[min(80vw,22rem)] max-md:opacity-[0.28] max-[700px]:right-[-8%] max-[700px]:max-w-[min(72vw,28rem)] max-[700px]:opacity-30"
              src={`${assets}/images/Finland-Vector.png`}
              alt=""
              width={640}
              height={800}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>

          <div className="relative z-[1] mt-[clamp(2rem,5vh,4rem)] flex max-w-[min(36rem,90%)] flex-col items-start gap-[clamp(0.5rem,1.2vh,0.9rem)] self-start text-left max-md:mt-[clamp(1.5rem,4vh,2.5rem)] max-md:w-full max-md:max-w-full">
            <h1 className="hero-title">{strings.TITLE.toUpperCase()}</h1>
            <p className="hero-tagline">{strings.DESCRIPTION.toUpperCase()}</p>
          </div>

          <div className="relative z-[1] flex w-full min-h-0 flex-col items-center justify-center py-[clamp(1rem,3vh,2rem)]">
            <HeroCountdown
              remainingSeconds={remainingSeconds}
              loading={loading}
              failed={failed}
              noContent={noContent}
              nextUpcomingMatch={nextUpcomingMatch}
              liveMatches={liveMatches}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
